import {getQueryClient} from '@github-ui/query-client'
import {
  useCachePreheatQueries,
  useCachePreheatQuery,
  useCachePreheatTask,
  useCachePreheatTasks,
} from '@github-ui/relay-query-cache/hooks/use-cache-preheat-query'
import {useAnalytics} from '@github-ui/use-analytics'
import {useCallback, useEffect, useMemo} from 'react'

import {issueViewerSecondaryViewQueryOptions} from '../components/fetch-issue-viewer-secondary-view'
import {IssueViewerViewGraphqlQuery, preheatIssueViewerSecondaryComponents} from '../components/IssueViewer'

type UseCachePreheatIssueViewerProps = {
  owner: string
  repo: string
  number: number
  source: string
  allowedOwner?: string
  enabled?: boolean
}

type UseCachePreheatIssueViewerQueriesProps = {
  issues: Array<{
    owner: string
    repo: string
    number: number
  }>
  source: string
}

export function useCachePreheatIssueViewer({
  owner,
  repo,
  number,
  source,
  allowedOwner,
  enabled = true,
}: UseCachePreheatIssueViewerProps) {
  const {sendAnalyticsEvent} = useAnalytics({optional: true})
  const excludeFromPrimaryQueryCacheKey = useMemo(() => ['allowedOwner'], [])

  const primaryVariables = useMemo(
    () => ({
      owner,
      repo,
      number,
      count: 15,
      allowedOwner,
    }),
    [owner, repo, number, allowedOwner],
  )

  const secondaryQueryOptions = useMemo(
    () =>
      issueViewerSecondaryViewQueryOptions({
        owner,
        repo,
        number,
        markAsRead: false,
        sendAnalyticsEvent,
        preheat: true,
      }),
    [number, owner, repo, sendAnalyticsEvent],
  )
  const preheatSecondaryQuery = useCallback(
    () => getQueryClient().prefetchQuery(secondaryQueryOptions),
    [secondaryQueryOptions],
  )

  useEffect(() => {
    if (!enabled) return
    void preheatIssueViewerSecondaryComponents()
  }, [enabled])

  useCachePreheatQuery({
    query: IssueViewerViewGraphqlQuery,
    variables: primaryVariables,
    source,
    excludeFromCacheKey: excludeFromPrimaryQueryCacheKey,
    highPriority: true,
    enabled,
  })

  useCachePreheatTask({
    key: JSON.stringify(secondaryQueryOptions.queryKey),
    task: preheatSecondaryQuery,
    enabled,
  })
}

export function useCachePreheatIssueViewerQueries({issues, source}: UseCachePreheatIssueViewerQueriesProps) {
  const {sendAnalyticsEvent} = useAnalytics({optional: true})
  const excludeFromPrimaryQueryCacheKey = useMemo(() => ['allowedOwner'], [])

  const preheatQueries = useMemo(
    () =>
      issues.map(({owner, repo, number}) => ({
        query: IssueViewerViewGraphqlQuery,
        variables: {
          owner,
          repo,
          number,
          count: 15,
        },
        source,
        excludeFromCacheKey: excludeFromPrimaryQueryCacheKey,
        highPriority: true,
      })),
    [issues, source, excludeFromPrimaryQueryCacheKey],
  )

  useEffect(() => {
    if (issues.length === 0) return
    void preheatIssueViewerSecondaryComponents()
  }, [issues.length])

  const secondaryPreheatTasks = useMemo(
    () =>
      issues.map(({owner, repo, number}) => {
        const options = issueViewerSecondaryViewQueryOptions({
          owner,
          repo,
          number,
          markAsRead: false,
          sendAnalyticsEvent,
          preheat: true,
        })
        return {
          key: JSON.stringify(options.queryKey),
          task: () => getQueryClient().prefetchQuery(options),
        }
      }),
    [issues, sendAnalyticsEvent],
  )

  useCachePreheatQueries({queries: preheatQueries})
  useCachePreheatTasks({tasks: secondaryPreheatTasks})
}
